import React, { useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { Accordion } from '../accordion/Accordion';
import { Attendee } from '../attendee/Attendee';
import { MatchingContacts } from '../matchingContacts/MatchingContacts';
import * as CalendarActions from '../../actions/calendar';
import * as RootActions from '../../actions/index';
import './Attendees.scss';
import { MissingAttendee } from '../missingAttendee/MissingAttendee';
import { getSalesforceContacts } from '../../../api/emailsApi/calendar';
import { BannerMessage } from '../bannerMessage/BannerMessage';
import officeApi from '../../../api/officeApi';

export const Attendees = () => {
  const dispatch = useDispatch();
  const selectedContacts = useSelector((state) => state.calendar.selectedContacts);
  const salesforceContactMap = useSelector((state) => state.calendar.salesforceContactMap);
  const multipleMatchedContacts = useSelector((state) => state.calendar.multipleMatchedContacts);
  const selectedOrganizer = useSelector((state) => state.calendar.selectedOrganizer);
  const multipleMatchedOrganizers = useSelector((state) => state.calendar.multipleMatchedOrganizers);
  const isComposingEvent = useSelector((state) => state.calendar.isComposingEvent);
  const outlookEventAttendees = useSelector((state) => state.calendar.eventAttendees);
  const attendeesToAdd = useSelector((state) => state.calendar.attendeesToAdd);
  const savedAttendees = useSelector((state) => state.calendar.savedAttendees);
  const existingAttendeeChange = useSelector((state) => state.calendar.attendeeChange);
  const attendeeBannerToggle = useSelector((state) => state.calendar.attendeeBannerToggle);
  const defaultAttendeeObject = useSelector((state) => state.mail.settings?.calendarSettings?.attendeeContactSettings?.defaultAttendeeObject);
  const isProxyOrganizer = (selectedOrganizer.emailAddress !== outlookEventAttendees.organizer) && !Object.keys(multipleMatchedOrganizers).length;
  const [isExpanded, setIsExpanded] = useState(true);
  const [missingContacts, setMissingContacts] = useState([]);

  const organizerEmail = Object.keys(multipleMatchedOrganizers).length ? Object.keys(multipleMatchedOrganizers)[0] : selectedOrganizer.emailAddress;

  const addAttendeeSpan = (
    <span className='add-attendee-span'
      onClick={() => {
        batch(() => {
          dispatch(CalendarActions.addingAttendee(true));
        });
      }}>
      Add Attendee
    </span>
  );
  const populateAttendees = () => {
    let attendeeChange = existingAttendeeChange;
    const attendeesProcessed = { [isProxyOrganizer ? selectedOrganizer.emailAddress : outlookEventAttendees.organizer]: true }; // keep track of who we've already rendered
    const multipleContactMap = {};
    const singleContactMap = {};
    const currentSavedMapping = {};
    const missingContactsArray = [];
    const savedAttendeesRemovedFromOutlook = [];

    for (const attendee of savedAttendees) {
      if (outlookEventAttendees?.required?.includes(attendee.email) || outlookEventAttendees?.optional?.includes(attendee.email)) {
        currentSavedMapping[attendee.email] = attendee;
      } else {
        savedAttendeesRemovedFromOutlook.push(attendee.email);
      }
    }

    const newSavedAttendees = savedAttendees.filter((attendee) => !savedAttendeesRemovedFromOutlook.includes(attendee.email));
    if (JSON.stringify(newSavedAttendees) !== JSON.stringify(savedAttendees)) {
      dispatch(CalendarActions.setSavedEventAttendees(savedAttendees.filter((attendee) => !savedAttendeesRemovedFromOutlook.includes(attendee.email))));
    }

    const recentlyAddedAttendees = Object.values(attendeesToAdd).map((attendeeToAdd) => attendeeToAdd.emailAddress);
    for (const emailAddress of [...outlookEventAttendees.required, ...outlookEventAttendees.optional, ...recentlyAddedAttendees]) {
      if (attendeesProcessed[emailAddress]) {
        continue;
      }
      if (!savedAttendees.find(({ email }) => email === emailAddress)) {
        attendeeChange = true;
      }
      if (currentSavedMapping[emailAddress]) {
        if (salesforceContactMap[emailAddress].length === 1) {
          singleContactMap[emailAddress] = salesforceContactMap[emailAddress][0];
        } else {
          const savedRecordId = currentSavedMapping[emailAddress].id;
          singleContactMap[emailAddress] = salesforceContactMap[emailAddress]?.find((record) => record.objectId === savedRecordId);
        }
      } else if (salesforceContactMap[emailAddress] && salesforceContactMap[emailAddress].length === 1) {
        attendeeChange = true;
        singleContactMap[emailAddress] = salesforceContactMap[emailAddress][0];
      } else if (salesforceContactMap[emailAddress] && salesforceContactMap[emailAddress].length > 1) {
        if (selectedContacts[emailAddress]) {
          attendeeChange = true;
          singleContactMap[emailAddress] = selectedContacts[emailAddress];
        } else {
          // ignore attendee change when multi-match contact is added
          attendeeChange = false;

          if (defaultAttendeeObject) {
            const defaultAttendees = salesforceContactMap[emailAddress].filter((record) => record.objectApiName === defaultAttendeeObject);
            if (defaultAttendees.length > 1) {
              multipleContactMap[emailAddress] = { email: emailAddress, contactList: defaultAttendees };
            } else if (defaultAttendees.length === 1) {
              attendeeChange = true;
              singleContactMap[emailAddress] = defaultAttendees[0];
            } else {
              multipleContactMap[emailAddress] = { email: emailAddress, contactList: salesforceContactMap[emailAddress] };
            }
          } else {
            multipleContactMap[emailAddress] = { email: emailAddress, contactList: salesforceContactMap[emailAddress] };
          }
        }
      } else {
        // ignore attendee change when missing contact is added
        attendeeChange = false;
        missingContactsArray.push(emailAddress);
      }
      attendeesProcessed[emailAddress] = true;
    }
    setMissingContacts(missingContactsArray);

    batch(() => {
      dispatch(CalendarActions.setSelectedContacts(singleContactMap));
      dispatch(CalendarActions.setMatchedContacts(multipleContactMap));
      dispatch(CalendarActions.setAttendeesToAdd({}));
      if (attendeeChange) {
        dispatch(CalendarActions.attendeeChange());
      }
      missingContactsArray.length ? dispatch(CalendarActions.isMissingContacts(true)) : dispatch(CalendarActions.isMissingContacts(false));
      dispatch(RootActions.isLoading(false));
      dispatch(CalendarActions.setAttendeeBanner(true));
    });
  };

  useEffect(() => {
    populateAttendees();
  }, [salesforceContactMap, outlookEventAttendees, savedAttendees]);

  const refreshHandler = async () => {
    dispatch(RootActions.isLoading(true));
    const [required, optional, organizer] = isComposingEvent ? await Promise.all([...officeApi.getComposeEventAttendees()]) :
      await Promise.all([...officeApi.getEventAttendees()]);
    const newEventAttendees = {
      organizer,
      required: Array.from(new Set([...required])),
      optional: Array.from(new Set([...optional])),
    };
    const attendeeMapping = await getSalesforceContacts([newEventAttendees.organizer, ...newEventAttendees.required, ...newEventAttendees.optional]);

    batch(() => {
      dispatch(CalendarActions.setSalesforceContactMap(attendeeMapping));
      dispatch(CalendarActions.setEventAttendees(newEventAttendees));
    });
  };

  return (
    <div id="calendar-attendees-component" >
      <Accordion title='Attendees' status={isExpanded} setStatus={setIsExpanded} link={isComposingEvent && addAttendeeSpan} refreshHandler={refreshHandler}/>
      {
        isExpanded && <div className='calendar-attendees-body'>
          {
            Object.keys(selectedOrganizer).length ?
              <Attendee
                key={selectedOrganizer.objectId}
                isOrganizer={true}
                details={selectedOrganizer}
              /> :
              <MatchingContacts
                key={organizerEmail}
                email={organizerEmail}
                contactList={multipleMatchedOrganizers[organizerEmail]}
                isOrganizer={true}
              />
          }
          {
            Object.values(selectedContacts).map((attendee) => (
              <Attendee
                isOrganizer={false}
                key={attendee.objectId}
                details={attendee}
              />
            ))
          }
          <BannerMessage
            displayBanner={(Object.values(multipleMatchedContacts).length > 0 || missingContacts.length > 0) && attendeeBannerToggle}
            closeBanner={() => dispatch(CalendarActions.setAttendeeBanner(false))}
          >
            The below must be resolved to be saved as Attendees on the Litify event.
          </BannerMessage>
          {
            Object.values(multipleMatchedContacts).map((contactListObj) => (
              <MatchingContacts
                key={contactListObj.email}
                email={contactListObj.email}
                contactList={contactListObj.contactList}
              />
            ))
          }
          {
            missingContacts.map((contact) => (
              <MissingAttendee
                key={contact}
                email={contact}
              />
            ))
          }
        </div>
      }
    </div>
  );
};
